import { LegalPlaceholder } from "@/components/layout/LegalPlaceholder";

/**
 * /cookie-policy - Whistll Cookie Policy (last updated April 2026).
 * Copy transcribed from ~/Documents/Whistll/whistll-all-documents/
 * whistll-cookie-policy.pdf. Keep in sync when legal updates anything.
 */
export function CookiePolicyPage() {
  return (
    <LegalPlaceholder title="Cookie Policy" lastUpdated="April 2026">
      <h2>1. What this policy covers</h2>
      <p>
        This Cookie Policy explains how Whistll ("we", "us", "our") uses
        cookies and similar tracking technologies on the Whistll website
        (whistll.com) and, where relevant, within the Whistll mobile
        application (the "App"). It applies to all users across the United
        Kingdom, United States, Canada, Australia, and New Zealand.
      </p>
      <p>
        This Policy should be read alongside the{" "}
        <a href="/privacy-policy" className="underline underline-offset-2">
          Whistll Privacy Policy
        </a>
        , which describes more generally how we collect and use personal
        data.
      </p>

      <h2>2. What cookies are</h2>
      <p>
        Cookies are small text files that websites place on your device
        when you visit them. They allow websites to recognise your device,
        remember your preferences, and understand how the site is being
        used. Some cookies are essential to the operation of the website.
        Others are used for analytics, functionality, or marketing
        purposes.
      </p>
      <p>
        Similar technologies (such as pixels, tags, local storage, and
        software development kits) work in comparable ways. Throughout this
        Policy, "cookies" refers to all such technologies.
      </p>

      <h2>3. Categories of cookies we use</h2>

      <h3>3.1 Strictly necessary cookies</h3>
      <p>
        These cookies are essential for the website to function. They
        enable basic features such as page navigation, session management,
        security, and access to secure areas of the site. These cookies do
        not require consent and cannot be disabled without breaking core
        functionality. Examples include:
      </p>
      <ul>
        <li>Session cookies that keep you logged in</li>
        <li>Security cookies that prevent fraud and abuse</li>
        <li>Load balancing cookies that help the site run smoothly</li>
        <li>Consent preference cookies that remember your cookie choices</li>
      </ul>

      <h3>3.2 Functional cookies</h3>
      <p>
        These cookies allow the website to remember choices you make and
        provide enhanced, personalised features. Examples include:
      </p>
      <ul>
        <li>Language or region preferences</li>
        <li>Display settings</li>
      </ul>
      <p>These cookies require consent where they are not strictly necessary.</p>

      <h3>3.3 Analytics and performance cookies</h3>
      <p>
        These cookies help us understand how visitors interact with the
        website by collecting and reporting information, usually in
        aggregate form. They help us improve how the site works and what
        we offer. Examples include:
      </p>
      <ul>
        <li>
          <strong>Vercel Analytics</strong> - measures page views, traffic
          sources, and performance
        </li>
        <li>
          <strong>Sentry</strong> - monitors errors and performance issues
          to help us fix bugs
        </li>
      </ul>
      <p>
        Analytics cookies require consent outside of the UK's updated PECR
        rules for purely aggregate analytics. We present them as optional
        and respect your choice.
      </p>

      <h3>3.4 Marketing and advertising cookies</h3>
      <p>
        These cookies track your activity across websites and are used to
        deliver targeted advertising or measure ad effectiveness. Whistll
        does not currently use marketing or advertising cookies. If this
        changes in the future, this Policy will be updated and your
        explicit consent will be required before any such cookies are set.
      </p>

      <h2>4. Third-party cookies</h2>
      <p>
        Some cookies are placed by third-party services we use. These
        third parties have their own privacy and cookie policies, which we
        encourage you to review. Third parties that may set cookies through
        Whistll include:
      </p>
      <ul>
        <li>
          <strong>Vercel</strong> - website hosting and analytics
        </li>
        <li>
          <strong>Sentry</strong> - error tracking and monitoring
        </li>
        <li>
          <strong>Stripe</strong> - payment processing (only triggered
          during checkout flows)
        </li>
      </ul>

      <h2>5. How we obtain consent</h2>
      <p>
        When you first visit whistll.com, you will see a cookie consent
        banner. The banner allows you to:
      </p>
      <ul>
        <li>
          <strong>Accept all</strong> cookies, including non-essential ones
        </li>
        <li>
          <strong>Reject all</strong> non-essential cookies
        </li>
        <li>
          <strong>Customise</strong> your preferences by cookie category
        </li>
      </ul>
      <p>
        Strictly necessary cookies are always active and do not require
        consent. All other cookies are only set after you have given
        consent for the relevant category.
      </p>
      <p>
        Your consent choice is remembered for a set period (typically 6
        months), after which we will ask you to review your preferences
        again. You can change your preferences at any time via the cookie
        settings link in the website footer.
      </p>

      <h2>6. Global Privacy Control (GPC)</h2>
      <p>
        In jurisdictions where the Global Privacy Control signal is
        recognised (including California, Colorado, Connecticut, and Oregon
        in the United States), Whistll honours GPC signals. If your browser
        or device sends a GPC signal, we treat this as an opt-out request
        for any data sharing for advertising purposes.
      </p>

      <h2>7. Country-specific positions</h2>
      <p>
        <strong>United Kingdom:</strong> Our cookie practices comply with
        the Privacy and Electronic Communications Regulations (PECR), as
        updated by the Data (Use and Access) Act 2025. From 5 February
        2026, analytics cookies used solely to collect aggregate statistics
        to improve a service no longer require prior consent under amended
        PECR rules. We may use such analytics cookies without requiring
        your consent. All other non-essential cookies continue to require
        consent.
      </p>
      <p>
        <strong>United States:</strong> Cookie usage is governed by
        applicable state privacy laws. Users in California, Colorado,
        Connecticut, Oregon, and other states with comprehensive privacy
        legislation may have additional rights, including the right to opt
        out of the sale or sharing of personal data for targeted
        advertising.
      </p>
      <p>
        <strong>European Union and EEA:</strong> Where applicable, our
        cookie practices comply with the ePrivacy Directive and GDPR.
      </p>
      <p>
        <strong>Canada:</strong> Cookie practices comply with PIPEDA and
        applicable provincial privacy legislation.
      </p>
      <p>
        <strong>Australia:</strong> Cookie practices comply with the
        Privacy Act 1988 and the Australian Privacy Principles.
      </p>
      <p>
        <strong>New Zealand:</strong> Cookie practices comply with the
        Privacy Act 2020.
      </p>

      <h2>8. How to manage cookies</h2>
      <p>You can manage cookies in several ways:</p>
      <p>
        <strong>Through our cookie consent banner:</strong> Change your
        preferences at any time by clicking the cookie settings link in the
        website footer.
      </p>
      <p>
        <strong>Through your browser settings:</strong> Most browsers allow
        you to block or delete cookies. Note that blocking strictly
        necessary cookies may break parts of the website.
      </p>
      <p>
        <strong>Through your device settings:</strong> On mobile devices,
        you can adjust tracking and advertising settings in your device
        privacy settings.
      </p>
      <p>Helpful links for managing cookies in common browsers:</p>
      <ul>
        <li>
          Chrome:{" "}
          <a href="https://www.google.com/chrome/" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
            chrome.com
          </a>
        </li>
        <li>
          Safari:{" "}
          <a href="https://support.apple.com" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
            support.apple.com
          </a>
        </li>
        <li>
          Firefox:{" "}
          <a href="https://www.mozilla.org" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
            mozilla.org
          </a>
        </li>
        <li>
          Edge:{" "}
          <a href="https://www.microsoft.com" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
            microsoft.com
          </a>
        </li>
      </ul>

      <h2>9. Changes to this policy</h2>
      <p>
        We may update this Cookie Policy from time to time to reflect
        changes in the cookies we use or in applicable law. Material
        changes will be highlighted on the website. The "Last updated" date
        at the top of this Policy reflects the most recent version.
      </p>

      <h2>10. Contact us</h2>
      <p>
        If you have any questions about this Cookie Policy or our use of
        cookies, please contact:
      </p>
      <p>
        <strong>Email:</strong>{" "}
        <a href="mailto:hello@whistll.com" className="underline underline-offset-2">
          hello@whistll.com
        </a>
        <br />
        <strong>Company number:</strong> 17172530
      </p>
      <p>We aim to respond within 3 working days.</p>
    </LegalPlaceholder>
  );
}
